// REST request/response bodies — docs/02-signaling-and-api.md §3 & §5.
// All under https://<endpoint>/v1, JSON, JWT bearer auth unless noted.

import { z } from 'zod';
import {
  AvatarSeed,
  B64Blob,
  CallId,
  DisplayName,
  Ed25519PublicKey,
  Ed25519Signature,
  Timestamp,
  Token,
  Uuid,
} from './primitives.js';
import {
  CallLogEntry,
  Friend,
  FriendRequest,
  PasskeySummary,
  Session,
  SessionKind,
  TokenBundle,
} from './models.js';
import { EndpointList } from './endpoints.js';

// --- Registration & auth ----------------------------------------------------

/** `POST /register` request. */
export const RegisterRequest = z
  .object({ publicKey: Ed25519PublicKey, displayName: DisplayName })
  .strict();
export type RegisterRequest = z.infer<typeof RegisterRequest>;

/** `POST /register` response. */
export const RegisterResponse = z.object({ userId: Uuid }).strict();
export type RegisterResponse = z.infer<typeof RegisterResponse>;

/** `POST /auth/challenge` request. */
export const AuthChallengeRequest = z.object({ userId: Uuid }).strict();
export type AuthChallengeRequest = z.infer<typeof AuthChallengeRequest>;

/**
 * `POST /auth/challenge` response — a nonce to sign. `kdfSalt` lets a fresh
 * device derive the password authKey before `POST /auth/password`; the server
 * always returns one (a deterministic decoy for unknown/passwordless accounts)
 * so its presence never reveals whether the account exists or has a password.
 */
export const AuthChallengeResponse = z
  .object({ nonce: B64Blob, kdfSalt: B64Blob })
  .strict();
export type AuthChallengeResponse = z.infer<typeof AuthChallengeResponse>;

/**
 * `POST /auth/verify` request — challenge-response proof.
 *
 * `kind` (optional, defaults to `'web'` server-side) records the device kind
 * of the session this login creates, so the Capacitor app's sessions list as
 * `'android'` rather than `'web'`.
 *
 * `authKey` (optional) is the SAME password proof `PasswordLoginRequest`
 * carries: when the account has a password set, a key login must ALSO prove it
 * (docs/03 §3a). A fresh device derives it from the typed password and the
 * `kdfSalt` already returned by `POST /auth/challenge`. Absent it (or wrong),
 * an account with a password answers `password_required` — identically in
 * both cases, never revealing whether the account has a password.
 */
export const AuthVerifyRequest = z
  .object({
    userId: Uuid,
    nonce: B64Blob,
    signature: Ed25519Signature,
    kind: SessionKind.optional(),
    authKey: B64Blob.optional(),
  })
  .strict();
export type AuthVerifyRequest = z.infer<typeof AuthVerifyRequest>;

/** `POST /auth/verify` response. */
export const AuthVerifyResponse = TokenBundle;
export type AuthVerifyResponse = z.infer<typeof AuthVerifyResponse>;

/**
 * `POST /auth/password` request — password login for a new session. `kind`
 * (optional, defaults to `'web'` server-side) records the created session's
 * device kind (see `AuthVerifyRequest`).
 */
export const PasswordLoginRequest = z
  .object({ userId: Uuid, authKey: B64Blob, kind: SessionKind.optional() })
  .strict();
export type PasswordLoginRequest = z.infer<typeof PasswordLoginRequest>;

/** `POST /auth/password` response — tokens + the encrypted identity-key blob. */
export const PasswordLoginResponse = z
  .object({
    accessToken: Token,
    refreshToken: Token,
    expiresIn: z.number().int().positive(),
    sessionId: Uuid,
    encIdentityKey: B64Blob,
  })
  .strict();
export type PasswordLoginResponse = z.infer<typeof PasswordLoginResponse>;

/** `POST /auth/refresh` request. */
export const RefreshRequest = z.object({ refreshToken: Token }).strict();
export type RefreshRequest = z.infer<typeof RefreshRequest>;

/** `POST /auth/refresh` response. */
export const RefreshResponse = TokenBundle;
export type RefreshResponse = z.infer<typeof RefreshResponse>;

// --- Account (me) -----------------------------------------------------------

/**
 * `PUT /me/password` request — atomic replace of all three password fields.
 * All three `null` removes the password. Guarded by a fresh identity-key
 * signature (docs/02 §3, docs/03 §3a).
 */
export const SetPasswordRequest = z
  .object({
    signature: Ed25519Signature,
    authHash: B64Blob.nullable(),
    kdfSalt: B64Blob.nullable(),
    encIdentityKey: B64Blob.nullable(),
    /** "Also terminate other sessions" — expected when a device is lost. */
    terminateOthers: z.boolean().optional(),
  })
  .strict();
export type SetPasswordRequest = z.infer<typeof SetPasswordRequest>;

/** `GET /me` response — own profile. */
export const MeResponse = z
  .object({
    userId: Uuid,
    publicKey: Ed25519PublicKey,
    displayName: DisplayName,
    hasPassword: z.boolean(),
    createdAt: Timestamp,
    /** Deterministic-avatar seed; absent ⇔ never chosen. */
    avatarSeed: AvatarSeed.optional(),
  })
  .strict();
export type MeResponse = z.infer<typeof MeResponse>;

/**
 * `PATCH /me` request — partial profile update. Both fields are optional, but
 * at least one must be present (a no-op PATCH is rejected). The "at least one"
 * rule is a zod refinement: it is NOT representable in the exported JSON Schema
 * (both fields simply appear optional there), so the Go server enforces it in
 * its handler — exactly like other cross-field invariants (docs/02 §3).
 */
export const UpdateMeRequest = z
  .object({
    displayName: DisplayName.optional(),
    avatarSeed: AvatarSeed.optional(),
  })
  .strict()
  .refine((v) => v.displayName !== undefined || v.avatarSeed !== undefined, {
    message: 'at least one of displayName or avatarSeed is required',
  });
export type UpdateMeRequest = z.infer<typeof UpdateMeRequest>;

/** `DELETE /me` request — fresh key-signature (+ password `authKey` if set). */
export const DeleteMeRequest = z
  .object({ signature: Ed25519Signature, authKey: B64Blob.optional() })
  .strict();
export type DeleteMeRequest = z.infer<typeof DeleteMeRequest>;

/**
 * `GET /me/aliases` response — the account's E2E-encrypted contact-alias blob
 * (docs/08 §8). Opaque to the server (like SDP): ciphertext of the alias map
 * under a seed-derived key (`SIG_DOMAIN.ALIAS_ENC`); the server can never read
 * an alias. Both fields null ⇔ never uploaded.
 */
export const AliasesBlobResponse = z
  .object({ blob: B64Blob.max(87400).nullable(), updatedAt: Timestamp.nullable() })
  .strict();
export type AliasesBlobResponse = z.infer<typeof AliasesBlobResponse>;

/** `PUT /me/aliases` request — replace the blob wholesale (LWW, ≤64 KiB raw). */
export const PutAliasesBlobRequest = z
  .object({ blob: B64Blob.max(87400) })
  .strict();
export type PutAliasesBlobRequest = z.infer<typeof PutAliasesBlobRequest>;

/** `PUT /me/aliases` response — server timestamp of the stored write. */
export const PutAliasesBlobResponse = z.object({ updatedAt: Timestamp }).strict();
export type PutAliasesBlobResponse = z.infer<typeof PutAliasesBlobResponse>;

// --- Friends & requests -----------------------------------------------------

/** `GET /friends` response. */
export const FriendsListResponse = z
  .object({ friends: z.array(Friend) })
  .strict();
export type FriendsListResponse = z.infer<typeof FriendsListResponse>;

/** `POST /friends/requests` request. */
export const SendFriendRequestRequest = z
  .object({ toUserId: Uuid })
  .strict();
export type SendFriendRequestRequest = z.infer<typeof SendFriendRequestRequest>;

/** `GET /friends/requests` response — incoming + outgoing pending. */
export const FriendRequestsListResponse = z
  .object({
    incoming: z.array(FriendRequest),
    outgoing: z.array(FriendRequest),
  })
  .strict();
export type FriendRequestsListResponse = z.infer<
  typeof FriendRequestsListResponse
>;

// --- Blocks -----------------------------------------------------------------

/** `GET /blocks` response — list of blocked UUIDs. */
export const BlocksListResponse = z
  .object({ blocked: z.array(Uuid) })
  .strict();
export type BlocksListResponse = z.infer<typeof BlocksListResponse>;

/** `POST /blocks` request. */
export const BlockRequest = z.object({ userId: Uuid }).strict();
export type BlockRequest = z.infer<typeof BlockRequest>;

// --- Call history -----------------------------------------------------------

/** `GET /calls` response — own call history, newest first. */
export const CallsListResponse = z
  .object({ calls: z.array(CallLogEntry) })
  .strict();
export type CallsListResponse = z.infer<typeof CallsListResponse>;

/** `POST /calls/seen` request — omit `callIds` to mark every missed call seen. */
export const CallsSeenRequest = z
  .object({ callIds: z.array(CallId).optional() })
  .strict();
export type CallsSeenRequest = z.infer<typeof CallsSeenRequest>;

// --- Sessions ---------------------------------------------------------------

/** Push-subscription material for a session. */
export const PushSubscription = z
  .object({
    kind: z.enum(['webpush', 'fcm', 'ntfy', 'apns']),
    /** Endpoint/subscription JSON or token, opaque to the protocol. */
    payload: z.string().min(1),
  })
  .strict();
export type PushSubscription = z.infer<typeof PushSubscription>;

/** `GET /sessions` response. */
export const SessionsListResponse = z
  .object({ sessions: z.array(Session) })
  .strict();
export type SessionsListResponse = z.infer<typeof SessionsListResponse>;

/** `PATCH /sessions/current` request — update label and/or push subscription. */
export const UpdateSessionRequest = z
  .object({
    label: z.string().max(128).optional(),
    /** `null` clears the push subscription. */
    push: PushSubscription.nullable().optional(),
  })
  .strict();
export type UpdateSessionRequest = z.infer<typeof UpdateSessionRequest>;

// --- Passkeys (WebAuthn) -----------------------------------------------------
//
// Passkeys mint SERVER sessions on an already-provisioned device; they do NOT
// restore the E2E identity (docs/03 §2 — the recovery phrase remains the
// account root). The WebAuthn creation/request options and the
// attestation/assertion responses are OPAQUE passthrough objects here: their
// exact shapes are owned by the browser's `navigator.credentials` and the
// server's WebAuthn library (go-webauthn), so — exactly like SDP is opaque
// server-side — the protocol only pins the wrapper and treats the WebAuthn
// blob as an arbitrary JSON object. A strict wrapper still rejects unknown
// top-level keys.

/**
 * An opaque WebAuthn JSON object (creation/request options, or an
 * attestation/assertion `PublicKeyCredential`). Deliberately unstructured —
 * the browser and go-webauthn own these shapes; validating them here would
 * duplicate (and inevitably drift from) their specs. Must be a JSON object.
 */
export const WebAuthnJson = z.record(z.string(), z.unknown());
export type WebAuthnJson = z.infer<typeof WebAuthnJson>;

/**
 * `POST /auth/passkeys/register/options` response — the opaque WebAuthn
 * creation options (`{publicKey: …}`) plus, when the server enabled the PRF
 * extension for cross-device identity restore (docs/03 §2), a random per-user
 * `prfSalt`. The client evaluates PRF over this salt, HKDFs the result into a
 * wrapping key (`SIG_DOMAIN.PASSKEY_PRF`), and encrypts the identity seed under
 * it — the salt is NOT secret, the PRF output (server-never-sees) is. Absent
 * `prfSalt` ⇒ this passkey will be session-only (no seed blob).
 */
export const PasskeyRegisterOptionsResponse = z
  .object({ publicKey: WebAuthnJson, prfSalt: B64Blob.optional() })
  .loose();
export type PasskeyRegisterOptionsResponse = z.infer<typeof PasskeyRegisterOptionsResponse>;

/**
 * `POST /auth/passkeys/register` request — attestation + optional label (auth'd).
 * `encIdentityKey` is the identity seed encrypted under the PRF-derived wrapping
 * key (docs/03 §2); present only when the authenticator supported PRF and the
 * client wrapped the seed. Absent ⇒ a session-only passkey (never restores a
 * new device). The server stores only this ciphertext, never the wrapping key.
 */
export const PasskeyRegisterRequest = z
  .object({
    credential: WebAuthnJson,
    label: DisplayName.optional(),
    encIdentityKey: B64Blob.optional(),
  })
  .strict();
export type PasskeyRegisterRequest = z.infer<typeof PasskeyRegisterRequest>;

/** `POST /auth/passkeys/register` response. */
export const PasskeyRegisterResponse = z.object({ passkey: PasskeySummary }).strict();
export type PasskeyRegisterResponse = z.infer<typeof PasskeyRegisterResponse>;

/** `GET /auth/passkeys` response — the caller's registered passkeys (auth'd). */
export const PasskeysListResponse = z
  .object({ passkeys: z.array(PasskeySummary) })
  .strict();
export type PasskeysListResponse = z.infer<typeof PasskeysListResponse>;

/**
 * `POST /auth/passkeys/login/options` request — no auth (pre-login).
 * `userId` targets one account's credentials and returns its per-user
 * `prfSalt` (the legacy flow). ABSENT `userId` begins a DISCOVERABLE
 * (usernameless) ceremony: empty allowCredentials, the authenticator offers
 * its resident Merkury passkeys and the assertion's userHandle names the
 * account — nothing typed. The discoverable flow has no per-user salt; the
 * client evaluates PRF over the fixed app-wide restore salt instead.
 */
export const PasskeyLoginOptionsRequest = z.object({ userId: Uuid.optional() }).strict();
export type PasskeyLoginOptionsRequest = z.infer<typeof PasskeyLoginOptionsRequest>;

/**
 * `POST /auth/passkeys/login/options` response — the opaque WebAuthn assertion
 * options (`{publicKey: …}`) plus the account's `prfSalt` when it has any
 * PRF-restorable passkey (docs/03 §2). A new device evaluates PRF over this
 * salt during the assertion; the matching credential's `encIdentityKey` comes
 * back from `POST /auth/passkeys/login`. Absent `prfSalt` ⇒ no passkey on this
 * account can restore the identity (fall back to the recovery phrase/password).
 */
export const PasskeyLoginOptionsResponse = z
  .object({ publicKey: WebAuthnJson, prfSalt: B64Blob.optional() })
  .loose();
export type PasskeyLoginOptionsResponse = z.infer<typeof PasskeyLoginOptionsResponse>;

/**
 * `POST /auth/passkeys/login` request — assertion + the created session's
 * `kind` (optional, defaults `'web'` server-side). No auth: this MINTS a
 * session, exactly like `POST /auth/verify`. `userId` matches the options
 * request that began the ceremony: present for the legacy targeted flow,
 * ABSENT for the discoverable flow (the server reads the account from the
 * verified assertion's userHandle).
 */
export const PasskeyLoginRequest = z
  .object({ userId: Uuid.optional(), credential: WebAuthnJson, kind: SessionKind.optional() })
  .strict();
export type PasskeyLoginRequest = z.infer<typeof PasskeyLoginRequest>;

/**
 * `POST /auth/passkeys/login` response — the token bundle every login returns,
 * plus (for a passkey that carries a wrapped seed) the asserted credential's
 * `encIdentityKey` ciphertext so a new device can decrypt the identity seed
 * locally (docs/03 §2), exactly like `PasswordLoginResponse`. Absent for a
 * session-only passkey.
 */
export const PasskeyLoginResponse = z
  .object({
    accessToken: Token,
    refreshToken: Token,
    expiresIn: z.number().int().positive(),
    sessionId: Uuid,
    encIdentityKey: B64Blob.optional(),
  })
  .strict();
export type PasskeyLoginResponse = z.infer<typeof PasskeyLoginResponse>;

// --- TURN --------------------------------------------------------------------

/** A single ICE (STUN/TURN) server, as returned by `POST /turn/credentials`. */
export const IceServer = z
  .object({
    urls: z.array(z.string().min(1)).min(1),
    username: z.string(),
    credential: z.string(),
  })
  .strict();
export type IceServer = z.infer<typeof IceServer>;

/** `POST /turn/credentials` response. */
export const TurnCredentialsResponse = z
  .object({
    iceServers: z.array(IceServer),
    ttlSeconds: z.number().int().positive(),
  })
  .strict();
export type TurnCredentialsResponse = z.infer<typeof TurnCredentialsResponse>;

// --- Endpoints & health ------------------------------------------------------

/** `GET /endpoints` response — the current signed endpoint list. */
export const EndpointsResponse = EndpointList;
export type EndpointsResponse = z.infer<typeof EndpointsResponse>;

/** `GET /healthz` response. */
export const HealthzResponse = z
  .object({ status: z.literal('ok'), time: Timestamp })
  .strict();
export type HealthzResponse = z.infer<typeof HealthzResponse>;
